import index from "../../../data/seed/knowledge-index.json";

/**
 * Kennisbank-artikelen uit Markdown-bestanden (src/content/kennisbank/*.md).
 * Elk bestand heeft frontmatter (title/category/description/answer/author/updated/image);
 * de body is gewone Markdown. Beheer = een .md-bestand toevoegen/bewerken — de
 * sitemap/prerender (scripts/generate-sitemap.mjs) ontdekt nieuwe slugs automatisch.
 *
 * Categorie = één van KNOWLEDGE_CATEGORIES (zie hieronder); de index groepeert daarop,
 * dus blijft de pagina overzichtelijk terwijl het aantal artikelen groeit.
 */
export interface Article {
  slug: string;
  title: string;
  /** Onderwerp-categorie voor de index — bij voorkeur één van KNOWLEDGE_CATEGORIES. */
  category: string;
  description: string;
  /** Direct antwoord (40–60 woorden) bovenaan — GEO best practice. */
  answer: string;
  author: string;
  /** ISO-datum (laatst herzien). */
  updated: string;
  /** Hero-/thumbnail-afbeelding (pad in /public of URL), optioneel. */
  image?: string;
  /** Alt-tekst bij de afbeelding. */
  imageAlt?: string;
  /** Geschatte leestijd in minuten (~200 woorden/min). */
  readingMinutes: number;
  /** Gerenderde HTML van de Markdown-body. */
  html: string;
  /** Genoemde producten (artikel-slugs, komma-gescheiden in frontmatter `products:`) → getoond
   *  als "Producten in dit artikel"-kaarten met een link naar de productpagina. */
  products?: string[];
  /** Interactieve rekentool bovenaan het artikel (frontmatter `tool:`), bv. "glasgewicht". */
  tool?: string;
}

/**
 * Vaste categorie-volgorde + korte omschrijving voor de index-secties. Nieuwe artikelen
 * krijgen in hun frontmatter één van deze `name`-waarden; alles daarbuiten komt onder "Overig".
 */
export const KNOWLEDGE_CATEGORIES: { name: string; description: string }[] = [
  { name: "Glas kiezen & bestellen", description: "Kiezen, opmeten, bewerken en bestellen — alles om met vertrouwen glas op maat te bestellen." },
  { name: "Isolatie & veiligheid", description: "HR++, triple, vacuümglas, gehard en gelaagd glas, U-waarde en veiligheidsnormen." },
  { name: "Renovatie & kozijnen", description: "Bestaand glas vervangen, monumenten, glaslatten en isolatie in oude kozijnen." },
  { name: "Producten & toepassingen", description: "Douchewand, spiegel, tafelblad, balustrade, deuren en meer — per toepassing uitgelegd." },
  { name: "Onderhoud & problemen", description: "Krassen, schoonmaken, condens, tocht en kapot of lek glas — herkennen en oplossen." },
];

/**
 * Alleen de metadata — de artikelteksten zitten hier bewust NIET in.
 *
 * Deze module hangt via de zoekbalk aan élke pagina. Met `eager: true` op de markdown-bestanden
 * (hoe het eerst was) zaten alle 55 artikelen in twee talen — zo'n 2,9 MB tekst — in de
 * hoofdbundel die iedere bezoeker downloadt. De lijstjes hebben alleen titel/omschrijving nodig;
 * de volledige tekst haalt de artikelpagina lazy op via `article-body.ts`.
 *
 * De index (data/seed/knowledge-index.json) wordt bij het bouwen gegenereerd uit de frontmatter
 * (scripts/build-knowledge-index.mjs). Nieuw artikel = .md toevoegen, zoals altijd.
 */
type IndexMeta = Omit<Article, "slug" | "html">;
const IDX = index as { nl: Record<string, IndexMeta>; en: Record<string, Partial<IndexMeta>> };

export const ARTICLES: Article[] = Object.entries(IDX.nl)
  .map(([slug, meta]) => ({ slug, ...meta, html: "" }))
  .sort((a, b) => (a.updated < b.updated ? 1 : -1)); // nieuwste eerst

export function getArticle(slug: string): Article | undefined {
  return ARTICLES.find((a) => a.slug === slug);
}

/** Engelse metadata-overrides per slug (title/description/answer/imageAlt). */
const EN_ARTICLES: Record<string, Partial<Article>> = IDX.en;

/**
 * Geeft het artikel in de gevraagde taal. Bij "en" worden titel/omschrijving/antwoord/HTML
 * overschreven met de Engelse variant (per veld terugval op NL). De categorie blijft de NL-key
 * (voor groepering); het label vertaal je in de UI met t().
 */
export function localizeArticle(a: Article, locale: "nl" | "en"): Article {
  if (locale !== "en") return a;
  const en = EN_ARTICLES[a.slug];
  return en ? { ...a, ...en } : a;
}

/** Artikelen gegroepeerd per categorie, in de vaste KNOWLEDGE_CATEGORIES-volgorde; onbekende
 *  categorieën komen onderaan als "Overig". Alleen groepen met artikelen worden teruggegeven. */
export function articlesByCategory(): { name: string; description: string; items: Article[] }[] {
  const order = KNOWLEDGE_CATEGORIES.map((c) => c.name);
  const groups = KNOWLEDGE_CATEGORIES.map((c) => ({ ...c, items: ARTICLES.filter((a) => a.category === c.name) }));
  const overig = ARTICLES.filter((a) => !order.includes(a.category));
  if (overig.length) groups.push({ name: "Overig", description: "Overige gidsen en uitleg.", items: overig });
  return groups.filter((g) => g.items.length > 0);
}

// Té generieke woorden filteren we uit auto-trefwoorden (matchen op alles / zeggen niets).
const KEYWORD_STOP = new Set(["glas", "voor", "met", "van", "het", "een", "onze", "alle", "maat", "soorten", "kopen", "bestellen"]);

/**
 * Bouwt automatische trefwoorden uit vrije tekst (bv. een categorienaam/-slug): lowercase,
 * splitst op spaties/koppeltekens, gooit stopwoorden en te korte woorden weg, dedupliceert.
 */
export function keywordsFromText(...parts: (string | undefined | null)[]): string[] {
  const words = parts
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/[^a-zà-ÿ0-9+\s-]/g, " ")
    .split(/[\s-]+/);
  return [...new Set(words.filter((w) => w.length >= 4 && !KEYWORD_STOP.has(w)))];
}

/**
 * Kennisbank-artikelen die het best bij een set trefwoorden passen — voor "Uit de kennisbank"-
 * blokken op landingspagina's. Score = trefwoord-treffers, zwaarder in de titel dan in
 * categorie/slug/omschrijving. Zonder treffers → leeg (blok verbergt zich dan).
 */
export function relatedArticles(keywords: string[], limit = 3, exclude: string[] = []): Article[] {
  const kw = [...new Set(keywords.map((k) => k.toLowerCase().trim()).filter(Boolean))];
  if (!kw.length) return [];
  const ex = new Set(exclude);
  return ARTICLES
    .filter((a) => !ex.has(a.slug))
    .map((a) => {
      const title = a.title.toLowerCase();
      const rest = `${a.category} ${a.slug.replace(/-/g, " ")} ${a.description}`.toLowerCase();
      let score = 0;
      for (const k of kw) {
        if (title.includes(k)) score += 3;
        else if (rest.includes(k)) score += 1;
      }
      return { a, score };
    })
    .filter((s) => s.score > 0)
    .sort((x, y) => y.score - x.score)
    .slice(0, limit)
    .map((s) => s.a);
}
